import React from 'react';
import {
  AccessibilityRole,
  Platform,
  StyleProp,
  Text,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native';
import { BlurView } from 'expo-blur';
import { createStyles, Theme } from '@/contexts/theme-context';
import { FONTS, shadow } from '@/styles';

export enum BadgeAlignment {
  BOTTOM = 'bottom',
  CENTER = 'center',
  TOP = 'top',
}

export enum BadgeSize {
  MEDIUM = 'medium',
  SMALL = 'small',
  XSMALL = 'xsmall',
  XXSMALL = 'xxsmall',
}

export enum BadgeVariant {
  BLURRED = 'blurred',
  BORDER = 'border',
  DEFAULT = 'default',
  DISABLED = 'disabled',
  ELEVATED = 'elevated',
  ERROR = 'error',
  INFO = 'info',
  SUCCESS = 'success',
  WARNING = 'warning',
}

interface BadgeProps {
  alignment?: `${BadgeAlignment}`;
  label: string;
  leftElement?: React.ReactNode;
  size?: `${BadgeSize}`;
  style?: StyleProp<ViewStyle>;
  textStyle?: TextStyle;
  variant?: `${BadgeVariant}`;
}

export const Badge: React.FC<BadgeProps> = ({
  alignment = BadgeAlignment.TOP,
  label,
  leftElement,
  size = BadgeSize.MEDIUM,
  variant = BadgeVariant.DEFAULT,
  style,
  textStyle,
}) => {
  const [styles, theme] = useStyles();

  const hasLeft = !!leftElement;
  const paddingStyle = getBadgePadding(size, hasLeft, label, variant);
  const isBlurred = variant === BadgeVariant.BLURRED;

  const containerStyle = [
    styles.base,
    getBadgeAlignment(alignment),
    styles.rounded,
    paddingStyle,
    getVariantContainerStyle(theme, variant),
    style,
  ];

  const combinedTextStyle = [
    FONTS.primarySemiBold,
    styles.textBase,
    textSizeStyles[size],
    getVariantTextStyle(theme, variant),
    textStyle,
  ];

  const content = (
    <>
      {leftElement ? (
        <View style={[styles.leftWrapper, getIconWrapperSize(size)]}>
          {leftElement}
        </View>
      ) : null}
      <Text numberOfLines={1} style={combinedTextStyle}>
        {label}
      </Text>
    </>
  );

  const sharedProps = {
    accessibilityLabel: label ? `${label} badge` : 'badge',
    accessibilityRole: 'text' as AccessibilityRole,
    accessibilityState: { disabled: false },
    accessible: !!label,
    importantForAccessibility: 'yes' as const,
    style: containerStyle,
  };

  if (isBlurred) {
    return (
      <BlurView
        {...sharedProps}
        experimentalBlurMethod="dimezisBlurView"
        intensity={75}
        tint="light"
      >
        {content}
      </BlurView>
    );
  }

  return <View {...sharedProps}>{content}</View>;
};

const getBadgePadding = (
  size: `${BadgeSize}`,
  hasLeftElement: boolean,
  label: string,
  variant?: `${BadgeVariant}`,
): ViewStyle => {
  const isSingleChar = label.length === 1;

  if (variant === BadgeVariant.ELEVATED) {
    return {
      // adjust padding for long text for elevated variant
      paddingHorizontal: label.length > 1 ? 4 : 8,
      paddingVertical: 2,
    };
  }

  if (size === BadgeSize.XSMALL || size === BadgeSize.XXSMALL) {
    if (hasLeftElement) {
      return {
        paddingLeft: 2,
        paddingRight: 8,
        paddingVertical: 2,
      };
    }
    return isSingleChar
      ? { paddingHorizontal: 5, paddingVertical: 0.5 }
      : { paddingHorizontal: 4, paddingVertical: 0.5 };
  }

  if (size === BadgeSize.SMALL) {
    return hasLeftElement
      ? { paddingLeft: 6, paddingRight: 8, paddingVertical: 2 }
      : { paddingHorizontal: 8, paddingVertical: 2 };
  }

  // medium
  return hasLeftElement
    ? { paddingLeft: 6, paddingRight: 10, paddingVertical: 2 }
    : isSingleChar
      ? { paddingHorizontal: 8, paddingVertical: 2 }
      : { paddingHorizontal: 10, paddingVertical: 2 };
};

const alignmentMap: Record<BadgeAlignment, ViewStyle['alignSelf']> = {
  [BadgeAlignment.BOTTOM]: 'flex-end',
  [BadgeAlignment.CENTER]: 'center',
  [BadgeAlignment.TOP]: 'flex-start',
};

const getBadgeAlignment = (alignment: `${BadgeAlignment}`) => ({
  alignSelf: alignmentMap[alignment],
});

const iconSizeMap: Record<BadgeSize, number> = {
  [BadgeSize.XXSMALL]: 12,
  [BadgeSize.XSMALL]: 16,
  [BadgeSize.SMALL]: 20,
  [BadgeSize.MEDIUM]: 24,
};

const getIconWrapperSize = (size: `${BadgeSize}`): ViewStyle => ({
  height: iconSizeMap[size],
  width: iconSizeMap[size],
});

const textSizeStyles: Record<BadgeSize, TextStyle> = {
  [BadgeSize.XXSMALL]: { fontSize: 12, lineHeight: 15 },
  [BadgeSize.XSMALL]: { fontSize: 12, lineHeight: 15 },
  [BadgeSize.SMALL]: { fontSize: 12, lineHeight: 15 },
  [BadgeSize.MEDIUM]: { fontSize: 14, lineHeight: 17.5 },
};

const getVariantContainerStyle = (
  theme: Theme,
  variant: `${BadgeVariant}`,
): ViewStyle => {
  if (variant === BadgeVariant.BORDER) {
    return {
      backgroundColor: 'transparent',
      borderColor: theme.sk.color.border.primary,
      borderWidth: 1,
    };
  }

  if (variant === BadgeVariant.BLURRED) {
    return {
      ...Platform.select({
        ios: {
          backgroundColor: `rgba(255, 255, 255, 0.55)`,
        },
      }),
      borderRadius: 4,
      overflow: 'hidden', // Fixing borderRadius with BlurView
    };
  }

  if (variant === BadgeVariant.ELEVATED) {
    // Background color of pill (to increase legibility)
    return {
      backgroundColor: theme.sk.color.bg.tertiary.default,
      borderColor: theme.sk.color.border.primary,
      borderRadius: 16,
      borderWidth: 1,
      ...shadow({
        elevation: 6,
        opacity: 0.05,
        radius: 12,
        x: 0,
        y: 6,
      }),
    };
  }

  return {
    backgroundColor: {
      [BadgeVariant.DEFAULT]: theme.sk.color.bg.secondary.default,
      [BadgeVariant.ERROR]: theme.sk.color.bg.error,
      [BadgeVariant.INFO]: theme.sk.color.bg.blue,
      [BadgeVariant.DISABLED]: theme.sk.color.bg.secondary.default,
      [BadgeVariant.SUCCESS]: theme.sk.color.bg.success,
      [BadgeVariant.WARNING]: theme.sk.color.bg.warning,
    }[variant],
  };
};

const getVariantTextStyle = (
  theme: Theme,
  variant: `${BadgeVariant}`,
): TextStyle => ({
  color: {
    [BadgeVariant.BLURRED]: theme.sk.color.text.primary,
    [BadgeVariant.ELEVATED]: theme.sk.color.text.primary,
    [BadgeVariant.DEFAULT]: theme.sk.color.text.primary,
    [BadgeVariant.ERROR]: theme.sk.color.text.error,
    [BadgeVariant.INFO]: theme.sk.color.text.blue,
    [BadgeVariant.BORDER]: theme.sk.color.text.secondary,
    [BadgeVariant.DISABLED]: theme.sk.color.text.disabled,
    [BadgeVariant.SUCCESS]: theme.sk.color.text.success,
    [BadgeVariant.WARNING]: theme.sk.color.text.warning,
  }[variant],
});

const useStyles = createStyles(() => ({
  base: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
  },
  leftWrapper: {
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 4,
  },
  rounded: {
    borderRadius: 16,
  },
  textBase: {
    textAlign: 'center',
  },
}));
